import React from 'react';
import { Check, Shield, Zap, Users, Database, Video, FolderSync, LucideIcon } from 'lucide-react';

interface Feature {
    icon: LucideIcon;
    title: string;
    description: string;
    highlights: string[];
}

const FeaturesGrid: React.FC = () => {
    const features: Feature[] = [
        {
            icon: Shield,
            title: "Enterprise Security",
            description: "Military-grade AES 256-bit encryption, two-factor authentication, and advanced ransomware protection keep your data fortress secure.",
            highlights: ["Zero-trust architecture", "Real-time threat detection", "Automated backups"]
        },
        {
            icon: Database,
            title: "Unlimited Scalability",
            description: "Start with terabytes, scale to petabytes. No per-user fees, no storage limits—just pure expansion capability as you grow.",
            highlights: ["Hot-swappable drives", "RAID redundancy", "Expansion units"]
        },
        {
            icon: Zap,
            title: "Blazing Performance",
            description: "10GbE network support, NVMe SSD caching, and optimized file protocols deliver enterprise-grade speed for your entire team.",
            highlights: ["SSD acceleration", "Link aggregation", "Intelligent caching"]
        },
        {
            icon: Users,
            title: "True Collaboration",
            description: "Built-in office suite, real-time document editing, calendar, chat, and project management—all hosted on your infrastructure.",
            highlights: ["Synology Drive", "Office suite", "Team sync"]
        },
        {
            icon: FolderSync,
            title: "Hybrid Cloud Ready",
            description: "Seamlessly integrate with existing cloud services while maintaining your primary data on-premise for complete control.",
            highlights: ["Multi-cloud sync", "Automated tiering", "Cloud backup"]
        },
        {
            icon: Video,
            title: "Surveillance Integration",
            description: "Transform your NAS into a professional surveillance system supporting thousands of cameras with AI-powered analytics.",
            highlights: ["8,300+ camera support", "AI detection", "Centralized monitoring"]
        }
    ];

    return (
        <div className="container mb-24 pt-6">
            <h3 className="text-3xl md:text-4xl font-bold text-center mb-10 md:mb-16">
                Enterprise Capabilities, Zero Recurring Fees
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {features.map((feature, index) => {
                    const IconComponent = feature.icon;
                    return (
                        <div
                            key={index}
                            className="bg-slate-900/50 border border-purple-600/15 rounded-2xl p-8 transition-all duration-300 hover:border-purple-600/40 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(147,51,234,0.15)] relative overflow-hidden group"
                        >
                            <div
                                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                style={{ background: 'radial-gradient(circle at top left, rgba(147, 51, 234, 0.05), rgba(37, 99, 235, 0.05), transparent)' }}
                            />

                            <div className="relative z-10">
                                <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center mb-5 shadow-[0_8px_24px_rgba(147,51,234,0.3)]">
                                    <IconComponent size={28} strokeWidth={2} color="white" />
                                </div>
                                <h4 className="text-xl font-bold mb-3">
                                    {feature.title}
                                </h4>
                                <p className="text-slate-400 leading-relaxed mb-5">
                                    {feature.description}
                                </p>
                                <div className="flex flex-col gap-3">
                                    {feature.highlights.map((highlight, hIndex) => (
                                        <div key={hIndex} className="flex items-center gap-2 text-sm text-slate-300">
                                            <div className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center flex-shrink-0">
                                                <Check size={14} className="text-white" />
                                            </div>
                                            <span>{highlight}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default FeaturesGrid;